var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import * as THREE from 'three';
import { OrbFactory } from '../../../orbs/OrbFactory';
import { global } from '../../../core/GlobalState';
import SceneManager from '../../../core/SceneManager';
import { connectOrbs } from '../../actions/ConnectActions';
import * as SpraySystem from '../../physics/SpraySystem';
import { getNestedOrbParser, } from './NestedOrbExpansionRegistry';
export function expandOrbNested(orb) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q;
    return __awaiter(this, void 0, void 0, function* () {
        const config = (_a = orb.orbConfig) === null || _a === void 0 ? void 0 : _a.nestedExpansion;
        if (!(config === null || config === void 0 ? void 0 : config.enabled)) {
            return { created: [] };
        }
        const parserKey = (_c = (_b = config.parser) !== null && _b !== void 0 ? _b : orb.orbType) !== null && _c !== void 0 ? _c : '';
        const parser = parserKey ? getNestedOrbParser(parserKey) : undefined;
        if (!parser) {
            console.warn(`[NestedOrbExpansion] No parser registered for key "${parserKey}"`);
            return { created: [] };
        }
        let result;
        try {
            result = yield parser({ orb, config });
        }
        catch (error) {
            console.error('[NestedOrbExpansion] Parser execution failed', error);
            return { created: [] };
        }
        if (!result || !result.orbs || result.orbs.length === 0) {
            return { created: [] };
        }
        const sceneManager = SceneManager.getInstance();
        const sceneName = (_d = global.orbManager.getSceneForOrb(orb)) !== null && _d !== void 0 ? _d : sceneManager.getCurrentSceneName();
        const fallbackSceneName = sceneManager.getCurrentSceneName();
        const scene = (_f = (_e = (sceneName && sceneManager.getScene(sceneName))) !== null && _e !== void 0 ? _e : (fallbackSceneName && sceneManager.getScene(fallbackSceneName))) !== null && _f !== void 0 ? _f : global.scene;
        const origin = (_h = (_g = orb.group) === null || _g === void 0 ? void 0 : _g.position.clone()) !== null && _h !== void 0 ? _h : new THREE.Vector3();
        const createdSpecs = [];
        for (const spec of result.orbs) {
            const data = (_j = spec.data) !== null && _j !== void 0 ? _j : {};
            const child = OrbFactory.createOrbByType(spec.orbType, spec.label, data);
            if (!child) {
                console.warn('[NestedOrbExpansion] Failed to create orb of type', spec.orbType);
                continue;
            }
            createdSpecs.push({ spec, orb: child });
        }
        if (createdSpecs.length === 0) {
            return { created: [] };
        }
        const sprayConfig = (_k = config.spray) !== null && _k !== void 0 ? _k : {};
        const sprayEnabled = sprayConfig.enabled !== false;
        if (sprayEnabled) {
            const sprayOptions = {
                sprayCoefficient: sprayConfig.sprayCoefficient,
                timeGap: sprayConfig.timeGap,
                baseVelocity: sprayConfig.baseVelocity,
                drag: sprayConfig.drag,
                scene,
                originAttachment: orb,
            };
            yield SpraySystem.sprayOrbsRefactored(origin, new THREE.Vector3(0, 0, 1), createdSpecs.map(({ orb: child }) => child), sprayOptions);
        }
        else {
            const scatterRadius = ((_m = (_l = orb.orbConfig) === null || _l === void 0 ? void 0 : _l.radius) !== null && _m !== void 0 ? _m : 120) * 1.6;
            const sceneToUse = sceneName !== null && sceneName !== void 0 ? sceneName : sceneManager.getCurrentSceneName();
            for (const { orb: child } of createdSpecs) {
                const offset = new THREE.Vector3((Math.random() - 0.5) * scatterRadius, (Math.random() - 0.5) * scatterRadius, (Math.random() - 0.5) * scatterRadius);
                child.setPosition(origin.clone().add(offset));
                global.orbManager.addOrbToScene(child, sceneToUse);
            }
        }
        yield connectParentToChildren(orb, createdSpecs, config);
        for (const { spec, orb: child } of createdSpecs) {
            if ((_o = spec.connectionSteps) === null || _o === void 0 ? void 0 : _o.length) {
                yield executeConnectionSteps(orb, createdSpecs, spec.connectionSteps, child);
            }
        }
        const connectionSteps = [
            ...((_p = result.connectionSteps) !== null && _p !== void 0 ? _p : []),
            ...((_q = config.connectionSteps) !== null && _q !== void 0 ? _q : []),
        ];
        if (connectionSteps.length > 0) {
            yield executeConnectionSteps(orb, createdSpecs, connectionSteps);
        }
        return { created: createdSpecs.map(({ orb: child }) => child) };
    });
}
function connectParentToChildren(parent, children, config) {
    var _a;
    return __awaiter(this, void 0, void 0, function* () {
        if (config.autoConnectParent === false) {
            return;
        }
        const fallbackProfile = config.parentConnectionProfile;
        for (const { spec, orb } of children) {
            if (spec.disableParentConnection)
                continue;
            if (!parent.group || !orb.group)
                continue;
            const profile = (_a = spec.parentConnectionProfile) !== null && _a !== void 0 ? _a : fallbackProfile;
            connectOrbs({ from: parent, to: orb, profile });
        }
    });
}
function executeConnectionSteps(parent, children, steps, self) {
    return __awaiter(this, void 0, void 0, function* () {
        for (const step of steps) {
            const fromSet = resolveSelector(step.from, parent, children, self);
            const toSet = resolveSelector(step.to, parent, children, self);
            if (fromSet.length === 0 || toSet.length === 0) {
                continue;
            }
            if (step.cartesian === false) {
                const n = Math.min(fromSet.length, toSet.length);
                for (let i = 0; i < n; i++) {
                    if (fromSet[i] === toSet[i])
                        continue;
                    connectOrbs({ from: fromSet[i], to: toSet[i], profile: step.edgeProfile });
                }
            }
            else {
                for (const a of fromSet) {
                    for (const b of toSet) {
                        if (a === b)
                            continue;
                        connectOrbs({ from: a, to: b, profile: step.edgeProfile });
                    }
                }
            }
        }
    });
}
function resolveSelector(selector, parent, children, self) {
    switch (selector.kind) {
        case 'parent':
            return parent.group ? [parent] : [];
        case 'self':
            return (self === null || self === void 0 ? void 0 : self.group) ? [self] : [];
        case 'allChildren':
            return children.map(({ orb }) => orb).filter(o => !!o.group);
        case 'orbType':
            return children
                .filter(({ orb }) => orb.orbType === selector.orbType)
                .map(({ orb }) => orb)
                .filter(o => !!o.group);
        case 'tag':
            return children
                .filter(({ spec }) => { var _a; return (_a = spec.tags) === null || _a === void 0 ? void 0 : _a.includes(selector.tag); })
                .map(({ orb }) => orb)
                .filter(o => !!o.group);
        case 'tagPrefix':
            return children
                .filter(({ spec }) => { var _a; return (_a = spec.tags) === null || _a === void 0 ? void 0 : _a.some(tag => tag.startsWith(selector.prefix)); })
                .map(({ orb }) => orb)
                .filter(o => !!o.group);
        default:
            return [];
    }
}
// Ensure built-in parsers are registered
import './parsers/DesmosNestedParser';
